/**
 * CabinEnrollmentGuard
 *
 * Kabin Memurluğu sayfaları için enrollment guard.
 * ProtectedRoute (login kontrolü) DIŞINDA, onun içinde kullanılır —
 * önce login, sonra enrollment.
 *
 * İzin kuralları:
 *   - admin / superadmin / instructor  → doğrudan izin (enrollment sorgusu bile açılmaz)
 *   - student + kayıtlı                → izin
 *   - student + kayıtsız (404)         → /dashboard'a yönlendir
 *   - yüklenirken                      → spinner (yanlış-yönlendirme/flicker yok)
 *   - ağ/5xx hatası                    → izin ver (kayıtlı öğrenciyi kilitleme;
 *                                        gerçek içerik backend'de enrollment kontrollü)
 *
 * courseId prop'u verilirse yalnızca o courseId === 'course_cabin_main_v1' iken
 * guard devreye girer; başka kurslar için children direkt render edilir (no-op).
 * Bu sayede genel /lesson/:courseId/:lessonId route'u guard'a sarılabilir ama
 * diğer kurs dersleri etkilenmez.
 */
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useCabinEnrolled from '../../hooks/cabin/useCabinEnrolled';

const CABIN_COURSE_ID = 'course_cabin_main_v1';

export default function CabinEnrollmentGuard({ children, courseId: courseIdProp }) {
  const { user } = useAuth();
  const params = useParams();

  // courseId; prop olarak verilebildiği gibi URL param'dan da alınabilir.
  // Prop öncelikli; yoksa useParams'tan al.
  const resolvedCourseId = courseIdProp ?? params.courseId;

  // Guard yalnızca kabin kursu için çalışır.
  // courseId biliniyorsa (prop ya da URL param) ve kabin kursu DEĞİLSE → no-op.
  // courseId bilinmiyorsa (undefined) → guard devreye girer (route kabin'e özgü).
  if (resolvedCourseId && resolvedCourseId !== CABIN_COURSE_ID) {
    return children;
  }

  // Rol tespiti
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isInstructor = user?.role === 'instructor' || isAdmin;
  const isStudent = !!user && !isInstructor;

  // Admin/eğitmen: enrollment kontrolüne gerek yok → anında izin.
  // (user null durumu ProtectedRoute tarafından /login'e yönlendirilmiş olacak;
  //  buraya null user ile gelinmez — ama gelirse de enrollment sorgusunu açma.)
  if (isInstructor) {
    return children;
  }

  // Student enrollment kontrolü — hook her koşulda çağrılmalı (hooks kuralı).
  // isStudent false ise (user null ya da bilinmeyen rol) enabled=false → sorgu açılmaz.
  return <CabinEnrollmentCheck isStudent={isStudent}>{children}</CabinEnrollmentCheck>;
}

/**
 * Hook'u ayrı bileşende tutmak Rules of Hooks'u korur:
 * useCabinEnrolled her render'da aynı sırada çağrılır.
 */
function CabinEnrollmentCheck({ isStudent, children }) {
  const { cabinEnrolled, cabinCheckLoading, cabinCheckError } = useCabinEnrolled(isStudent);

  // Yüklenirken spinner — karar netleşmeden yönlendirme yok
  if (isStudent && cabinCheckLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Ağ/5xx hatası → öğrenciyi kilitleme, erişime izin ver.
  // Kayıtlı öğrencinin geçici hata yüzünden /dashboard'a atılmasını önler.
  // 404 bu dalı HİÇ tetiklemez (useCabinEnrolled'da 404 → false döner, throw etmez).
  if (isStudent && cabinCheckError) {
    return children;
  }

  // Kayıtlı öğrenci (404-dışı, kesin true) → izin
  if (isStudent && cabinEnrolled) {
    return children;
  }

  // Kesin kayıtsız (cabinEnrolled=false, 404'ten geldi) → /dashboard
  // veya user null (ProtectedRoute'dan geçmeden geldiyse)
  return <Navigate to="/dashboard" replace />;
}
